import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { MapPin } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

// Free stock photos (Unsplash) — real people in urban / civic contexts.
const PHOTOS = [
    {
        url: "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=700&q=80",
        tag: "Milano",
        span: "md:col-span-2 md:row-span-2",
    },
    {
        url: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=700&q=80",
        tag: "Roma",
        span: "md:col-span-2",
    },
    {
        url: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=700&q=80",
        tag: "Napoli",
        span: "md:col-span-1",
    },
    {
        url: "https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=700&q=80",
        tag: "Torino",
        span: "md:col-span-1",
    },
    {
        url: "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=700&q=80",
        tag: "Bologna",
        span: "md:col-span-2",
    },
];

export default function Community() {
    const { t } = useT();
    return (
        <section
            data-testid="sn-community"
            className="bg-sn-paper-2 py-24 md:py-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("community.eyebrow")}
                    </span>
                    <span className="hairline flex-1" />
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-ink-2 hidden md:block">
                        {t("community.caption")}
                    </span>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl text-sn-ink"
                >
                    {t("community.headline_a")}{" "}
                    <em className="italic text-sn-green">
                        {t("community.headline_b")}
                    </em>
                    .
                </motion.h2>

                <div className="mt-16 grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[210px] gap-3 md:gap-4">
                    {PHOTOS.map((p, i) => (
                        <motion.figure
                            key={i}
                            initial={{ opacity: 0, scale: 0.96 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
                            className={`group relative overflow-hidden rounded-md bg-sn-black ${p.span || ""}`}
                        >
                            <img
                                src={p.url}
                                alt={`Community · ${p.tag}`}
                                loading="lazy"
                                onError={(e) => {
                                    e.currentTarget.style.opacity = "0";
                                }}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                            <figcaption className="absolute bottom-3 left-3 flex items-center gap-1.5 font-mono-e text-[9px] uppercase tracking-[0.2em] text-white/90">
                                <MapPin size={11} className="text-sn-green" />
                                {p.tag}
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
}
